// =============================================================
// AnimatedKpiCard
// -------------------------------------------------------------
// Premium KPI tile with:
//   * Smooth number-counter animation on mount / value change
//   * Subtle hover lift
//   * Optional trend chip (▲ / ▼ vs previous period)
//   * Click handler for drill-down navigation
// Maintains the existing dashboard skin (rounded 14px, subtle
// shadow, brand colour accent). Designed to be re-used across
// every role in the Training Institute analytics dashboard.
// =============================================================

import React, { useEffect, useRef, useState } from 'react';

// Hook: animates a numeric value from 0 (or prev) to `target` over
// `duration` ms using requestAnimationFrame + easeOutCubic. Respects
// the `format` callback for currency/percent/etc.
function useAnimatedNumber(target, { duration = 900, format = (v) => Math.round(v).toLocaleString() } = {}) {
  const [value, setValue] = useState(0);
  const startRef = useRef(0);
  const fromRef  = useRef(0);
  const rafRef   = useRef(null);

  useEffect(() => {
    const numeric = Number(target);
    if (!Number.isFinite(numeric)) {
      setValue(target);
      return;
    }
    fromRef.current = value;
    startRef.current = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - startRef.current) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      const v = fromRef.current + (numeric - fromRef.current) * eased;
      setValue(v);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  return format(value);
}

export default function AnimatedKpiCard({
  icon,
  label,
  value,
  format,
  sub,
  color = '#6366f1',
  bg,
  trend,        // number (% change) — undefined hides the chip
  trendLabel,
  onClick,
  prefix,
  suffix,
  loading = false,
}) {
  const display = useAnimatedNumber(loading ? 0 : (Number.isFinite(Number(value)) ? value : 0), {
    duration: 900,
    format: (v) => {
      if (loading) return '—';
      if (!Number.isFinite(Number(value))) return value;
      if (format) return format(v);
      return Math.round(v).toLocaleString();
    },
  });
  const bgColor = bg || color + '18';
  const positive = (trend || 0) >= 0;
  const interactive = !!onClick;

  return (
    <div
      className="card h-100"
      style={{
        borderRadius: 14,
        border: `1px solid ${color}22`,
        cursor: interactive ? 'pointer' : 'default',
        boxShadow: '0 2px 6px rgba(15,23,42,0.04)',
        transition: 'transform .18s ease, box-shadow .18s ease',
        overflow: 'hidden',
        position: 'relative',
      }}
      onClick={onClick}
      onMouseEnter={interactive ? (e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 8px 24px ${color}22`;
      } : undefined}
      onMouseLeave={interactive ? (e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 6px rgba(15,23,42,0.04)';
      } : undefined}
    >
      <div className="card-body" style={{ padding: '16px 18px' }}>
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div
            className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 44, height: 44, background: bgColor }}
          >
            <i className={`bi ${icon}`} style={{ fontSize: 20, color }}></i>
          </div>
          {trend !== undefined && trend !== null && (
            <span
              style={{
                fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20,
                background: positive ? '#d1fae5' : '#fee2e2',
                color: positive ? '#166534' : '#991b1b',
                display: 'inline-flex', alignItems: 'center', gap: 3,
                whiteSpace: 'nowrap',
              }}
              title={trendLabel}
            >
              <i className={`bi bi-arrow-${positive ? 'up' : 'down'}-short`}></i>
              {Math.abs(trend)}%
            </span>
          )}
        </div>
        <div style={{ marginTop: 12, fontSize: 24, fontWeight: 800, color: '#1e293b', lineHeight: 1.1, minHeight: 28 }}>
          {prefix}{display}{suffix}
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
      </div>
      {/* Brand-tinted bottom accent bar */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3, background: color, opacity: 0.7 }} />
    </div>
  );
}
